/* eslint-disable max-lines, react/jsx-no-bind */
import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useTranslation } from '@utils/i18n/i18n';

import Button from '@App/components/Button/Button';
import QuestionNextButton from '@App/components/QuestionNextButton/QuestionNextButton';
import AnswerRanking from '@App/components/AnswerRanking/AnswerRanking';
import Keyboard from '@App/components/Keyboard/Keyboard';
import Search from '@App/components/Search/Search';
import QuestionProgress from '@App/components/QuestionProgress/QuestionProgress';
import theme from '@style/theme.json';
import formVisuals from '@App/components/QuestionFormVisuals/question-form-visuals';

import questionOptions from '@App/components/Map/data/question-options';

import '@App/pages/Tablet/Question1/question1-form.css';

function QuestionChoiceForm({
  questionId,
  titleKey,
  inputPlaceholderKey,
  resultsTitleKey,
  nextRoute,
  nextButtonKey = 'arc-layer-next-button',
}) {
  const { onSelectAnswer } = useOutletContext();
  const { t } = useTranslation();
  const { tabletId } = useParams();
  const isTablet = Boolean(tabletId);
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);

  const options = questionOptions[questionId].filter(option => (
    option.label.toLocaleLowerCase('nl-BE').includes(inputValue.trim().toLocaleLowerCase('nl-BE'))
  )).slice(0, 20);

  useEffect(() => {
    setSelectedValue(current => (
      current && current.label === inputValue ? current : null
    ));
  }, [inputValue]);

  function submitAnswer() {
    onSelectAnswer(questionId, selectedValue);
    setAnswerSubmitted(true);
    setIsKeyboardOpen(false);
  }

  function goToNextQuestion() {
    navigate(nextRoute);
  }

  function handleInputFocus() {
    setIsKeyboardOpen(true);
  }

  function handleInputPointerDown() {
    setIsKeyboardOpen(true);
  }

  function handleInputChange(event) {
    setInputValue(event.target.value);
    setSelectedValue(null);
  }

  function handleSelectOption(item) {
    setSelectedValue(item);
  }

  function handleKeyboardClose() {
    setIsKeyboardOpen(false);
    setKeyboardHeight(0);
  }

  const inputRootProps = {
    'data-keyboard-target': true,
    onFocus: handleInputFocus,
    onPointerDown: handleInputPointerDown,
  };
  const visual = formVisuals[questionId];
  const formStyle = {
    '--form-accent': visual.accent,
    '--form-accent-soft': visual.accentSoft,
    '--keyboard-height': `${keyboardHeight || 196}px`,
  };

  return (
    <div
      className={`question1-form question1-form--${questionId}${isTablet && isKeyboardOpen ? ' question1-form--keyboard-open' : ''}`}
      style={formStyle}
    >
      <div className="question1-form__body">
        <QuestionProgress questionId={questionId} />
        {!answerSubmitted ? (
          <>
            <h2 className={`question1-form__body__title question1-form__body__title--tablet-${tabletId}`}>
              <span>{t(titleKey)}</span>
            </h2>
            <Search
              value={inputValue}
              rootElProps={inputRootProps}
              onInputChange={handleInputChange}
              onSelect={handleSelectOption}
              options={options}
              selectedValue={selectedValue}
              placeholder={t(inputPlaceholderKey)}
              resultsTitle={t(resultsTitleKey)}
              color={
                Number(tabletId) === 1 ? theme?.color?.primary?.main : theme?.color?.secondary?.main
              }
            />
            <div className="question1-form__body__footer">
              <Button disabled={!selectedValue} onClick={submitAnswer}>
                {t('arc-layer-submit-button')}
              </Button>
            </div>
          </>
        ) : (
          <div className="question1-form__thank-you">
            <h2 className={`question1-form__body__title question1-form__body__title--tablet-${tabletId}`}>
              <span>{t('arc-layer-thank-you')}</span>
            </h2>
            <p className="question1-form__thank-you-text">{t('arc-layer-thank-you-text')}</p>
            <AnswerRanking questionId={questionId} selectedAnswer={selectedValue} />
            <div className="question1-form__body__footer">
              <QuestionNextButton onNext={goToNextQuestion}>
                {t(nextButtonKey)}
              </QuestionNextButton>
            </div>
          </div>
        )}
      </div>
      {isTablet && (
        <Keyboard
          isOpen={isKeyboardOpen}
          onClose={handleKeyboardClose}
          onHeightChange={setKeyboardHeight}
          value={inputValue}
          onChange={setInputValue}
        />
      )}
    </div>
  );
}

export default QuestionChoiceForm;
